import { LogLevel } from '@amplitude/analytics-types';

import config from 'js/app/config';
import logger from 'js/app/loggerSingleton';
import appInfo from 'js/lib/appInfo';
import * as cookie from 'js/lib/cookie';
import thirdParty from 'js/lib/thirdParty';
import { get as getUser } from 'js/lib/user';

import epicClient from 'bundles/epic/clients/FrontendInfra';

// This is a public API key (not a secret key). Safe to use in client-side code.
const API_KEY = '9f370ddd4287ab67b08ab58273ddf40c';
// See: https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2
const AMPLITUDE_ANALYTICS_CDN_URL = `https://cdn.amplitude.com/script/${API_KEY}.js`;

/**
 * Declare the window.amplitude object (set by Amplitude CDN script).
 * window.engagement is set by the engagement CDN script; we access it without extending Window to avoid type conflicts.
 */
declare global {
  interface Window {
    amplitude?: {
      init: (apiKey: string, options?: { autoCapture?: boolean; logLevel?: LogLevel }) => void;
      setUserId: (userId: string | null) => void;
      setDeviceId: (deviceId: string) => void;
      track: (eventType: string, eventProperties?: Record<string, any>) => void;
    };
  }
}

/**
 * Load and initialize Amplitude Analytics SDK
 */
async function loadAmplitudeAnalytics(): Promise<void> {
  // Check if already loaded
  if (window.amplitude) {
    return;
  }

  // Load the Amplitude Analytics SDK from CDN
  try {
    await thirdParty.loadScript({
      url: AMPLITUDE_ANALYTICS_CDN_URL,
    });
  } catch (error) {
    logger.error('Failed to load Amplitude Analytics SDK:', error);
    throw error; // Re-throw to be handled by outer catch
  }

  // Initialize Amplitude Analytics
  const amplitude = (window as any).amplitude as Window['amplitude'];
  if (amplitude && typeof amplitude.init === 'function') {
    amplitude.init(API_KEY, {
      autoCapture: false,
      logLevel: LogLevel.Debug,
    });
  } else {
    throw new Error('Amplitude SDK loaded but init method not found');
  }
}

/**
 * Initialize Amplitude Engagement SDK for Guides and Surveys until event-pulse supports Guides and Surveys events.
 * Initializes the Amplitude analytics browser SDK to track guides and surveys events
 * Use Amplitude engagement SDK init() and boot() instead of adding plugin to analytics SDK to prevent autocapture of events.
 *
 * Initialization is async and non-blocking - failures are handled to prevent breaking the app.
 *
 * @returns Promise resolving to void
 */
async function initializeEngagement(): Promise<void> {
  try {
    // Get user and device ID.
    const user = getUser();
    const deviceId = cookie.get('__204u') ?? '';
    if (!deviceId || !user?.id) {
      return;
    }

    // Load Amplitude Analytics SDK
    await loadAmplitudeAnalytics();
    if (!window.amplitude) {
      return;
    }
    // Set user and device ID in Amplitude Analytics
    window.amplitude.setUserId(String(user.id));
    window.amplitude.setDeviceId(deviceId);

    // Load the engagement SDK and initialize it
    const { init: engagementInit } = await import('@amplitude/engagement-browser');
    engagementInit(API_KEY, {
      serverZone: 'US',
    });
    if (!window.engagement) {
      return;
    }

    // Boot the engagement SDK
    await window.engagement.boot({
      user: {
        // eslint-disable-next-line camelcase
        user_id: String(user.id),
        // eslint-disable-next-line camelcase
        device_id: deviceId,
        // eslint-disable-next-line camelcase
        user_properties: {},
      },
      integrations: [
        {
          track: (event) => {
            // Only track guides and surveys events
            if (!window.amplitude || !event.event_type.startsWith('[Guides-Surveys]')) {
              return;
            }
            if (config.environment === 'development') {
              logger.info('Amplitude Engagement SDK track event:', event);
            } else {
              window.amplitude.track(event.event_type, event.event_properties);
            }
          },
        },
      ],
    });
  } catch (error) {
    logger.error('Failed to initialize Amplitude Engagement SDK:', error);
  }
}

/**
 * Initializes Amplitude Engagement SDK and analytics SDK for Guides and Surveys
 * if the current app is in the Epic allowlist (appsWithGuidesAndSurveys).
 */
const setupAmplitudeGuidesAndSurveys = (): undefined | Promise<void> => {
  const appName = appInfo.get().appName;
  if (!appName) {
    return undefined;
  }
  const appsWithGuidesAndSurveys = epicClient.get('appsWithGuidesAndSurveys') ?? [];
  if (appsWithGuidesAndSurveys.includes(appName)) {
    return initializeEngagement().catch(logger.warn);
  }
  return undefined;
};

export default setupAmplitudeGuidesAndSurveys;
